import React, { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Play, RotateCw, Music, Volume2 } from 'lucide-react'
import { MELODIES, TONES } from './data.js'
import { playNotes, playTone } from '../lib/audio.js'
import { tr, useLang } from '../lib/lang.js'
import { LangToggle } from '../lib/LangToggle.jsx'
import { rngFrom, shuffle } from '../lib/random.js'
import { awardBp } from '../lib/store.js'

export function MelodiasGame({ onBack, onComplete }) {
  const [lang, setLang] = useLang()
  const [mode, setMode] = useState(null) // null | 'guess' | 'tones'
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
        >
          <ArrowLeft size={16} /> Volver
        </button>
        <LangToggle lang={lang} onChange={setLang} />
      </div>
      {mode == null && (
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-[#181024] to-[#0c0a14] border border-purple-900/40">
            <div className="text-2xl">🎵</div>
            <h2 className="text-lg font-black text-white mt-1">Melodías</h2>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              {tr(
                lang,
                'Reconoce canciones por sus primeras notas, o repite secuencias de tonos de oído.',
                'Recognize songs from their first notes, or repeat tone sequences by ear.',
              )}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={() => setMode('guess')}
              className="text-left p-4 rounded-2xl bg-[#0e131f] border border-slate-800 hover:border-purple-500/50 cursor-pointer"
            >
              <Music size={20} className="text-purple-300" />
              <div className="text-sm font-bold text-white mt-2">
                {tr(lang, 'Adivina la melodía', 'Guess the melody')}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {tr(lang, 'Escucha y elige la canción.', 'Listen and pick the song.')}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode('tones')}
              className="text-left p-4 rounded-2xl bg-[#0e131f] border border-slate-800 hover:border-purple-500/50 cursor-pointer"
            >
              <Volume2 size={20} className="text-purple-300" />
              <div className="text-sm font-bold text-white mt-2">
                {tr(lang, 'Repite el tono', 'Repeat the tone')}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {tr(lang, 'Memoria auditiva.', 'Auditory memory.')}
              </div>
            </button>
          </div>
        </div>
      )}
      {mode === 'guess' && (
        <GuessMode lang={lang} onBack={() => setMode(null)} />
      )}
      {mode === 'tones' && (
        <TonesMode lang={lang} onBack={() => setMode(null)} />
      )}
    </div>
  )
}

function GuessMode({ lang, onBack }) {
  const [roundKey, setRoundKey] = useState(0)
  const [index, setIndex] = useState(0)
  const [score, setScore] = useState(0)
  const [played, setPlayed] = useState(false)
  const [locked, setLocked] = useState(null)
  const [done, setDone] = useState(false)
  const [playing, setPlaying] = useState(false)

  const deck = useMemo(() => {
    const rng = rngFrom((roundKey + 1) * 26573)
    return shuffle(rng, MELODIES).slice(0, 6).map((m) => {
      const others = shuffle(rng, MELODIES.filter((x) => x.id !== m.id)).slice(0, 3)
      const options = shuffle(rng, [m, ...others])
      return { melody: m, options }
    })
  }, [roundKey])

  const item = deck[index]

  const replay = () => {
    setPlayed(true)
    setPlaying(true)
    const ms = playNotes(item.melody.notes, { noteDur: 0.36 })
    setTimeout(() => setPlaying(false), ms)
  }

  const choose = (m) => {
    if (locked != null) return
    const correct = m.id === item.melody.id
    setLocked({ correct, id: m.id })
    if (correct) setScore((s) => s + 1)
    setTimeout(() => {
      if (index + 1 >= deck.length) {
        const fs = score + (correct ? 1 : 0)
        awardBp(fs * 15)
        setDone(true)
      } else {
        setIndex((i) => i + 1)
        setLocked(null)
        setPlayed(false)
      }
    }, 900)
  }

  const restart = () => {
    setRoundKey((k) => k + 1)
    setIndex(0)
    setScore(0)
    setLocked(null)
    setPlayed(false)
    setDone(false)
  }

  if (done) {
    return (
      <Result
        lang={lang}
        score={score}
        total={deck.length}
        onRestart={restart}
        onExit={onBack}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span className="font-bold uppercase tracking-wider">{tr(lang, 'Adivina la melodía', 'Guess the melody')}</span>
        <span>
          {index + 1}/{deck.length} · {score} ✓
        </span>
      </div>

      <button
        type="button"
        onClick={replay}
        className={`w-full py-10 rounded-3xl border flex flex-col items-center gap-3 transition-all cursor-pointer ${
          playing ? 'bg-purple-950/40 border-purple-500/50' : 'bg-[#0e131f] border-slate-800 hover:border-purple-500/50'
        }`}
      >
        <Play size={36} className={playing ? 'text-purple-300 animate-pulse' : 'text-purple-400'} />
        <span className="text-sm font-black text-white">
          {played ? tr(lang, 'Reproducir otra vez', 'Play again') : tr(lang, 'Reproducir', 'Play')}
        </span>
      </button>

      <div className="grid grid-cols-1 gap-2.5">
        {item.options.map((m) => {
          const isAnswer = m.id === item.melody.id
          const picked = locked?.id === m.id
          const cls =
            locked == null
              ? 'bg-[#131a29] border-slate-700 hover:border-purple-500/60 text-white'
              : isAnswer
                ? 'bg-emerald-950/60 border-emerald-500/50 text-emerald-200'
                : picked
                  ? 'bg-rose-950/60 border-rose-500/50 text-rose-200'
                  : 'bg-[#0e131f] border-slate-800 text-slate-500'
          return (
            <button
              key={m.id}
              type="button"
              disabled={!played || locked != null}
              onClick={() => choose(m)}
              className={`px-4 py-3.5 rounded-xl border text-sm font-bold text-left transition-all cursor-pointer disabled:cursor-default disabled:opacity-60 ${cls}`}
            >
              {tr(lang, m.name.es, m.name.en)}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function TonesMode({ lang, onBack }) {
  const [level, setLevel] = useState(3)
  const [lives, setLives] = useState(2)
  const [sequence, setSequence] = useState([])
  const [answer, setAnswer] = useState([])
  const [phase, setPhase] = useState('show') // show | input | end
  const [best, setBest] = useState(0)
  const [step, setStep] = useState(-1)

  const begin = (len) => {
    const rng = rngFrom(Date.now() % 1000000 + len * 31)
    const seq = Array.from({ length: len }, () => Math.floor(rng() * TONES.length))
    setSequence(seq)
    setAnswer([])
    setPhase('show')
    setStep(-1)
  }

  useEffect(() => {
    if (phase === 'show' && sequence.length) {
      const run = async () => {
        await new Promise((r) => setTimeout(r, 400))
        sequence.forEach((t, i) => {
          setTimeout(() => {
            setStep(i)
            playTone(TONES[t].note)
          }, i * 520)
        })
        setTimeout(() => {
          setPhase('input')
          setStep(-1)
        }, sequence.length * 520 + 300)
      }
      run()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sequence])

  useEffect(() => {
    begin(3)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tap = (i) => {
    if (phase !== 'input') return
    playTone(TONES[i].note, 0.25)
    const next = [...answer, i]
    setAnswer(next)
    if (next.length >= sequence.length) {
      const ok = next.every((v, k) => v === sequence[k])
      if (ok) {
        setBest((b) => Math.max(b, sequence.length))
        awardBp(sequence.length * 6)
        setTimeout(() => begin(sequence.length + 1), 700)
      } else {
        const rem = lives - 1
        setLives(rem)
        if (rem <= 0) setTimeout(() => setPhase('end'), 700)
        else setTimeout(() => begin(sequence.length), 700)
      }
    }
  }

  const restart = () => {
    setLives(2)
    setBest(0)
    begin(3)
  }

  if (phase === 'end') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center space-y-5">
        <Music size={40} className="text-purple-400" />
        <h2 className="text-xl font-black text-white">
          {tr(lang, 'Alcance tonal', 'Tone span')}: {best}
        </h2>
        <div className="flex gap-2 w-full max-w-xs">
          <button
            type="button"
            onClick={restart}
            className="flex-1 py-3 rounded-xl bg-purple-500 text-white font-black text-sm cursor-pointer"
          >
            {tr(lang, 'Otra vez', 'Again')}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
          >
            {tr(lang, 'Salir', 'Exit')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span className="font-bold uppercase tracking-wider">{tr(lang, 'Repite el tono', 'Repeat the tone')}</span>
        <span>
          {tr(lang, 'Nivel', 'Level')} {level} · {lives} ♥
        </span>
      </div>

      <div className="p-6 rounded-2xl bg-[#0e131f] border border-slate-800 text-center min-h-[16vh] flex items-center justify-center">
        <p className="text-sm font-bold text-slate-300">
          {phase === 'show'
            ? tr(lang, 'Escucha…', 'Listen…')
            : tr(lang, `Repite ${sequence.length} tonos`, `Repeat ${sequence.length} tones`)}
        </p>
      </div>

      <div className="grid grid-cols-5 gap-2">
        {TONES.map((t, i) => (
          <button
            key={t.note}
            type="button"
            disabled={phase !== 'input'}
            onClick={() => tap(i)}
            className={`py-6 rounded-2xl border font-black text-sm transition-all cursor-pointer disabled:opacity-50 ${
              step === i
                ? 'bg-purple-500/40 border-purple-400 text-white'
                : 'bg-[#131a29] border-slate-700 text-slate-200 hover:border-purple-500/50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="text-center text-[10px] text-slate-500">
        {answer.length}/{sequence.length}
      </div>
    </div>
  )
}

function Result({ lang, score, total, onRestart, onExit }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center space-y-5">
      <Music size={40} className="text-purple-400" />
      <h2 className="text-xl font-black text-white">
        {score}/{total}
      </h2>
      <p className="text-xs text-slate-400">{tr(lang, 'Melodías acertadas', 'Melodies guessed')}</p>
      <div className="flex gap-2 w-full max-w-xs">
        <button
          type="button"
          onClick={onRestart}
          className="flex-1 py-3 rounded-xl bg-purple-500 text-white font-black text-sm cursor-pointer flex items-center justify-center gap-2"
        >
          <RotateCw size={16} /> {tr(lang, 'Otra vez', 'Again')}
        </button>
        <button
          type="button"
          onClick={onExit}
          className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
        >
          {tr(lang, 'Salir', 'Exit')}
        </button>
      </div>
    </div>
  )
}
